import { ReactNode, useMemo, useState, useEffect } from 'react';
import cn from 'classnames';
import { Alert, Layout } from 'antd';
import { ReactComponent as MenuFoldOutlined } from 'resources/images/icons/menuCollapser.svg';
import { ReactComponent as MenuIcon } from 'resources/images/icons/menuCollapser.svg';
import { ReactComponent as CloseIcon } from 'resources/images/icons/menuCollapser.svg';
import { Link } from 'react-router-dom';
import { useStoreState } from 'easy-peasy';
import { Routes } from 'constants/routes';
import { useAuthRole, useWallet } from 'hooks';
import { IMainLayoutMenuLink } from 'types/components/layouts';
import FooterView from 'views/common/footer/FooterView';
import MainLayoutContext from './BasicMainLayoutContext';

import { ReactComponent as MainLogoCollapsed } from 'resources/images/carbon-collapsed.svg';
import { ReactComponent as MainLogo } from 'resources/images/carbon.svg';
import { UserRole } from 'constants/user';
import { IStoreModel } from 'types';
import styles from './BasicMainLayoutView.module.scss';
import { IconSvg, NavMenu } from 'components';

interface IProps {
  sidebarActions?: ReactNode;
  menuLinks?: Array<IMainLayoutMenuLink>;
  children?: ReactNode;
}

const BasicMainLayoutView = ({ children, sidebarActions, menuLinks = [] }: IProps) => {
  const { userRole } = useAuthRole({ isRedirect: false });
  const { walletAddress } = useWallet({ isRedirect: false });
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [isSidebarVisible, setIsSidebarVisible] = useState<boolean>(false);
  const [subFooter, setSubFooter] = useState<ReactNode | null>(null);
  const [backgroundImage, setBackgroundImage] = useState<string>('');

  const isConnected = useStoreState<IStoreModel>((store) => store.blockchain.wallet.isConnected);
  const isAuthorized = useStoreState<IStoreModel>((store) => store.auth.isAuthorized);

  const { Sider, Content } = Layout;

  // Check if device is mobile on component mount and resize
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
      // Auto-hide sidebar on mobile when resizing to mobile
      if (window.innerWidth < 768) {
        setIsSidebarVisible(false);
      } else {
        setIsSidebarVisible(true);
      }
    };

    // Initial check
    checkIfMobile();

    // Add event listener
    window.addEventListener('resize', checkIfMobile);

    // Clean up
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  const menuItems = useMemo(() => {
    return menuLinks.filter(({ roles, isWalletRequired }) => {
      const isSuperAdmin = userRole === UserRole.Superadmin;
      const isAllowedByRole = !roles || (userRole && roles.includes(userRole));
      const isAllowedByWallet = !isWalletRequired || !!walletAddress;

      if (isSuperAdmin && !isConnected) {
        return false;
      }

      return isAllowedByRole && isAllowedByWallet;
    });
  }, [menuLinks, userRole, walletAddress]);

  const context = useMemo(
    () => ({
      setSubFooter,
      setBackgroundImage,
    }),
    [setBackgroundImage, setSubFooter],
  );

  const toggleSidebar = () => {
    setIsSidebarVisible(!isSidebarVisible);
  };

  const handleSidebarClick = () => {
    if (isMobile) {
      setIsSidebarVisible(false);
    }
  };

  return (
    <MainLayoutContext.Provider value={context}>
      <div
        className={styles.backgroundImage}
        style={{
          backgroundImage: `linear-gradient(180deg, #FFFFFF 0%, #EDF5F6 46.17%)`,
        }}
      >
        <div className={styles.backgroundGradient}></div>
      </div>
      <div className={styles.mainGradient}>
        <div className={styles.container}>
          <Layout className={styles.layout}>
            {/* Mobile toggle button */}
            {isMobile && (
              <button className={styles.mobileToggle} onClick={toggleSidebar}>
                {isSidebarVisible ? (
                  <CloseIcon className={styles.toggleIcon} />
                ) : (
                  <MenuIcon className={styles.toggleIcon} />
                )}
              </button>
            )}

            {/* Overlay for mobile when sidebar is visible */}
            {isMobile && isSidebarVisible && (
              <div className={styles.overlay} onClick={() => setIsSidebarVisible(false)} />
            )}

            <Sider
              trigger={null}
              collapsible
              collapsed={isCollapsed && !isMobile}
              className={cn(styles.slider, {
                [styles.mobileVisible]: isSidebarVisible && isMobile,
                [styles.mobileHidden]: !isSidebarVisible && isMobile,
              })}
              onClick={handleSidebarClick}
            >
              <div className={cn(styles.logoContainer, { [styles.logoContainerCollapsed]: isCollapsed && !isMobile })}>
                {isCollapsed && !isMobile ? (
                  <div className={styles.collapsedLogo}>
                    <Link to={Routes.home}>
                      <MainLogoCollapsed />
                    </Link>
                    <div className={styles.collapsedTrigger} onClick={() => setIsCollapsed(!isCollapsed)}>
                      <IconSvg icon='arrowRight' />
                    </div>
                  </div>
                ) : (
                  <div className={styles.logoWrapper}>
                    <Link to={Routes.home}>
                      <MainLogo className={styles.white} />
                    </Link>
                    {!isMobile && (
                      <MenuFoldOutlined onClick={() => setIsCollapsed(!isCollapsed)} className={styles.menuCollapser} />
                    )}
                  </div>
                )}
              </div>
              {isAuthorized && (
                <div className={cn(styles.labelBtnContainer, { [styles.hide]: isCollapsed && !isMobile })}>
                  {sidebarActions}
                </div>
              )}
              <NavMenu items={menuItems} />
            </Sider>
            <Layout className={styles.layoutContainer}>
              <Content
                className={cn(styles.content, {
                  [styles.mobileContent]: isMobile,
                })}
              >
                {children}
              </Content>
              <FooterView>{subFooter}</FooterView>
            </Layout>
          </Layout>
        </div>
      </div>
    </MainLayoutContext.Provider>
  );
};

export default BasicMainLayoutView;
